import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Info,
  Filter,
} from 'lucide-react';
import { Card } from '../App';
import { fetchCardData, getCardImageUrl, useResolvedCardImage, ScryfallCard } from '../lib/scryfall';
import { deckService, DeckRecord } from '../lib/supabase';
import { BrewTooltip } from './BrewTooltip';

interface SuggestedCardsSectionProps {
  deck: Card[];
  sideboard: Card[];
  format?: string;
  onAddCard?: (card: Card) => void;
  onAddToSideboard?: (card: Card) => void;
}

export interface ComboSuggestion {
  id: string;
  missingCardName: string;
  missingCardImageUrl?: string;
  missingCardTypeLine?: string;
  deckPartnerNames: string[];
  produces: string[];
  description: string;
  prerequisites: string;
  popularity: number;
  manaNeeded?: string;
  identity: string;
  priceUsd?: string;
}

export interface ActiveDeckCombo {
  id: string;
  cardNames: string[];
  produces: string[];
  description: string;
  prerequisites: string;
  popularity: number;
}

export interface CoOccurrenceSuggestion {
  name: string;
  slug: string;
  numDecks: number;
  potentialDecks: number;
  inclusionRate: number; // 0..100
  maxLift: number;
  pairedWith: string[];
  category: string;
  archetypeLabel?: string;
  deckstrDecksCount: number;
  imageUrl?: string;
  typeLine?: string;
  manaCost?: string;
  oracleHint?: string;
}

export type StapleRole = 'All' | 'Removal' | 'Ramp' | 'Draw' | 'Wipes' | 'Protection & Tutors';

export interface StapleSuggestion {
  name: string;
  role: Exclude<StapleRole, 'All'>;
  manaCost: string;
  cmc: number;
  typeLine: string;
  colorIdentity: string[];
  imageUrl: string;
  numDecks?: number;
  inclusionRate?: number;
  oracleSummary: string;
}

export interface ArchetypeDef {
  id: string;
  label: string;
  icon: string;
  edhrecTag: string;
  description: string;
  detectPatterns: RegExp[];
}

export const ARCHETYPE_DEFINITIONS: ArchetypeDef[] = [
  {
    id: 'lifegain',
    label: 'Lifegain',
    icon: '💖',
    edhrecTag: 'lifegain',
    description: 'Payoffs and triggers for gaining life, draining opponents, and life-total scaling.',
    detectPatterns: [/gain.*life/i, /whenever you gain life/i, /lifelink/i],
  },
  {
    id: 'mill',
    label: 'Mill',
    icon: '🌀',
    edhrecTag: 'mill',
    description: 'Putting cards from libraries into graveyards and scaling off large graveyards.',
    detectPatterns: [/\bmill\b/i, /puts? the top .* cards? of .* library into .* graveyard/i],
  },
  {
    id: 'group-slug',
    label: 'Group Slug',
    icon: '💀',
    edhrecTag: 'group-slug',
    description: 'Punisher effects, symmetrical damage, and taxing opponents for taking game actions.',
    detectPatterns: [/each opponent loses/i, /deals? .* damage to each opponent/i, /whenever an opponent casts/i, /whenever a player/i],
  },
  {
    id: 'crime',
    label: 'Crime (Targeting)',
    icon: '🎯',
    edhrecTag: 'crime',
    description: 'Payoffs and repeatable engines for targeting opponents, their permanents, spells, or graveyards.',
    detectPatterns: [/commit a crime/i, /whenever you commit a crime/i, /target opponent/i, /target creature an opponent controls/i],
  },
  {
    id: 'control',
    label: 'Control',
    icon: '🛡️',
    edhrecTag: 'control',
    description: 'Countermagic, instant-speed interaction, board control, and late-game inevitability.',
    detectPatterns: [/counter target/i, /return target .* to its owner's hand/i, /destroy all/i],
  },
  {
    id: 'group-hug',
    label: 'Group Hug',
    icon: '🤝',
    edhrecTag: 'group-hug',
    description: 'Political effects, shared card draw, extra mana/land drops for the table, and pillowfort.',
    detectPatterns: [/each player draws/i, /each player may/i, /can't attack you unless/i, /an opponent may/i],
  },
  {
    id: 'aristocrats',
    label: 'Aristocrats / Sac',
    icon: '🩸',
    edhrecTag: 'aristocrats',
    description: 'Sacrifice outlets, death triggers, blood artist effects, and recursive fodder.',
    detectPatterns: [/whenever .* creature .* dies/i, /sacrifice a creature/i, /sacrifice another/i],
  },
  {
    id: 'reanimator',
    label: 'Reanimator',
    icon: '⚰️',
    edhrecTag: 'reanimator',
    description: 'Self-mill, entomb effects, and returning high-impact creatures from graveyards to the battlefield.',
    detectPatterns: [/from .* graveyard to the battlefield/i, /return target creature card from your graveyard/i],
  },
  {
    id: 'tokens',
    label: 'Tokens',
    icon: '👥',
    edhrecTag: 'tokens',
    description: 'Creating creature tokens, token doublers, populate, and go-wide anthem payoffs.',
    detectPatterns: [/create .* creature token/i, /tokens you control/i, /populate/i],
  },
  {
    id: 'spellslinger',
    label: 'Spellslinger',
    icon: '⚡',
    edhrecTag: 'spellslinger',
    description: 'Instant and sorcery triggers, magecraft, storm, spell copying, and cost reduction.',
    detectPatterns: [/whenever you cast an instant or sorcery/i, /instant and sorcery spells you cast/i, /magecraft/i, /copy target instant or sorcery/i],
  },
  {
    id: 'landfall',
    label: 'Landfall',
    icon: '🌱',
    edhrecTag: 'landfall',
    description: 'Landfall triggers, playing extra lands per turn, and sacrificing/recurring lands.',
    detectPatterns: [/whenever a land enters/i, /landfall/i, /play an additional land/i],
  },
  {
    id: 'counters',
    label: '+1/+1 Counters',
    icon: '➕',
    edhrecTag: 'plus-1-plus-1-counters',
    description: 'Putting +1/+1 counters on creatures, proliferate, and counter-scaling payoffs.',
    detectPatterns: [/\+1\/\+1 counter/i, /proliferate/i],
  },
  {
    id: 'blink',
    label: 'Blink / Flicker',
    icon: '✨',
    edhrecTag: 'blink',
    description: 'Exiling and returning permanents to repeatedly trigger enters-the-battlefield abilities.',
    detectPatterns: [/exile .* return .* to the battlefield/i, /when .* enters, /i],
  },
  {
    id: 'treasure',
    label: 'Treasure',
    icon: '🪙',
    edhrecTag: 'treasure',
    description: 'Generating Treasure tokens and payoffs for sacrificing artifacts/treasures.',
    detectPatterns: [/treasure token/i],
  },
  {
    id: 'artifacts',
    label: 'Artifacts',
    icon: '⚙️',
    edhrecTag: 'artifacts',
    description: 'Artifact synergies, affinity, artificers, and noncreature artifact engines.',
    detectPatterns: [/artifacts you control/i, /whenever an artifact/i, /affinity for artifacts/i],
  },
  {
    id: 'enchantress',
    label: 'Enchantress',
    icon: '📜',
    edhrecTag: 'enchantress',
    description: 'Constellation, drawing cards from casting enchantments, and enchantment scaling.',
    detectPatterns: [/whenever you cast an enchantment/i, /enchantments you control/i, /constellation/i],
  },
  {
    id: 'stax',
    label: 'Stax / Tax',
    icon: '🔒',
    edhrecTag: 'stax',
    description: 'Resource denial, rule-setting hatebears, and slowing down opponents.',
    detectPatterns: [/spells .* cost .* more to cast/i, /players can't/i, /don't untap during/i],
  },
  {
    id: 'voltron',
    label: 'Voltron / Equip',
    icon: '🗡️',
    edhrecTag: 'voltron',
    description: 'Auras, Equipment, and buffing a single threat for lethal damage.',
    detectPatterns: [/equipped creature/i, /enchanted creature/i, /aura and equipment/i],
  },
  {
    id: 'wheels',
    label: 'Wheels / Discard',
    icon: '🎡',
    edhrecTag: 'wheels',
    description: 'Forcing players to discard and draw new hands, punishing draws and discards.',
    detectPatterns: [/each player discards .* and draws/i, /whenever an opponent discards/i, /whenever an opponent draws/i],
  },
  {
    id: 'infect',
    label: 'Infect / Poison',
    icon: '☠️',
    edhrecTag: 'infect',
    description: 'Poison counters, toxic, infect, and proliferate synergies.',
    detectPatterns: [/\binfect\b/i, /\btoxic\b/i, /poison counter/i],
  },
];

// Known generic format staples that should go in the Staples section, NOT the Synergy/Combo section
const GENERIC_STAPLE_NAMES = new Set([
  'sol ring',
  'arcane signet',
  'command tower',
  'exotic orchard',
  'reliquary tower',
  'swords to plowshares',
  'path to exile',
  'counterspell',
  'swan song',
  'arcane denial',
  'negate',
  'fierce guardianship',
  'mana drain',
  'force of will',
  'force of negation',
  'pact of negation',
  'cyclonic rift',
  'rhystic study',
  'mystic remora',
  'smothering tithe',
  'esper sentinel',
  'teferis protection',
  "teferi's protection",
  'farewell',
  'wrath of god',
  'supreme verdict',
  'vanquish the horde',
  'austere command',
  'generous gift',
  'stroke of midnight',
  'enlightened tutor',
  'demonic tutor',
  'vampiric tutor',
  'imperial seal',
  'diabolic intent',
  'grim tutor',
  'toxic deluge',
  'damnation',
  'feed the swarm',
  'infernal grasp',
  'go for the throat',
  'deadly rollick',
  'dark ritual',
  'cabal coffers',
  'urborg, tomb of yawgmoth',
  'bojuka bog',
  'nights whisper',
  "night's whisper",
  'sign in blood',
  'phyrexian arena',
  'black market connections',
  'necropotence',
  'chaos warp',
  'blasphemous act',
  'vandalblast',
  'deflecting swat',
  'jeskas will',
  "jeska's will",
  'gamble',
  'faithless looting',
  'lightning bolt',
  'abrade',
  'Dockside Extortionist'.toLowerCase(),
  'beast within',
  'cultivate',
  'kodamas reach',
  "kodama's reach",
  'rampant growth',
  'farseek',
  'natures lore',
  "nature's lore",
  'three visits',
  'skyshroud claim',
  'birds of paradise',
  'llanowar elves',
  'elvish mystic',
  'fyndhorn elves',
  'heroic intervention',
  'eternal witness',
  'reclamation sage',
  'krosan grip',
  'natures claim',
  "nature's claim",
  'worldly tutor',
  'sylvan library',
  'the great henge',
  'guardian project',
  'garruks uprising',
  "garruk's uprising",
  'anguished unmaking',
  'despark',
  'vindicate',
  'void rend',
  'dovin\'s veto',
  'dovins veto',
  'assassins trophy',
  "assassin's trophy",
  'abrupt decay',
  'putrefy',
  'mortify',
  'terminate',
  'bedevil',
  'rakdos charm',
  'boros charm',
  'simic growth chamber',
  'chromatic lantern',
  'fellwar stone',
  'mind stone',
  'thought vessel',
  'commander\'s sphere',
  'commanders sphere',
  'wayfarers bauble',
  "wayfarer's bauble",
  'solemn simulacrum',
  'swiftfoot boots',
  'lightning greaves',
  'skullclamp',
  'senseis divining top',
  "sensei's divining top",
  'ancient tomb',
  'mana crypt',
  'mana vault',
  'chrome mox',
  'mox diamond',
  'jeweled lotus',
]);

function isGenericStapleCard(name: string, lift: number): boolean {
  const lower = name.toLowerCase().trim();
  if (GENERIC_STAPLE_NAMES.has(lower)) return true;
  if (lower.endsWith(' signet') || lower.startsWith('talisman of ')) return true;
  // Low-lift cards (< 1.85) with high generic inclusion are general staples, not build-around synergies
  if (lift < 1.85) return true;
  return false;
}

// Convert card name to EDHREC URL slug
function toEdhrecSlug(name: string): string {
  const frontFace = name.includes('//') ? name.split('//')[0].trim() : name.trim();
  return frontFace
    .toLowerCase()
    .replace(/['"’,]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function formatCompactNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(num >= 10_000 ? 0 : 1)}k`;
  return num.toLocaleString();
}

function getColorIdentityName(colors: Set<string>): string {
  const ordered = ['W', 'U', 'B', 'R', 'G'].filter((c) => colors.has(c)).join('');
  const names: Record<string, string> = {
    '': 'Colorless',
    W: 'Mono-White (W)',
    U: 'Mono-Blue (U)',
    B: 'Mono-Black (B)',
    R: 'Mono-Red (R)',
    G: 'Mono-Green (G)',
    WU: 'Azorius (W/U)',
    WB: 'Orzhov (W/B)',
    WR: 'Boros (W/R)',
    WG: 'Selesnya (W/G)',
    UB: 'Dimir (U/B)',
    UR: 'Izzet (U/R)',
    UG: 'Simic (U/G)',
    BR: 'Rakdos (B/R)',
    BG: 'Golgari (B/G)',
    RG: 'Gruul (R/G)',
    WUB: 'Esper (W/U/B)',
    WUR: 'Jeskai (W/U/R)',
    WUG: 'Bant (W/U/G)',
    WBR: 'Mardu (W/B/R)',
    WBG: 'Abzan (W/B/G)',
    WRG: 'Naya (W/R/G)',
    UBR: 'Grixis (U/B/R)',
    UBG: 'Sultai (U/B/G)',
    URG: 'Temur (U/R/G)',
    BRG: 'Jund (B/R/G)',
    WUBR: 'Yore-Tiller (W/U/B/R)',
    WUBG: 'Witch-Maw (W/U/B/G)',
    WURG: 'Ink-Treader (W/U/R/G)',
    WBRG: 'Dune-Brood (W/B/R/G)',
    UBRG: 'Glint-Eye (U/B/R/G)',
    WUBRG: '5-Color (WUBRG)',
  };
  return names[ordered] || ordered || 'All Colors';
}

function classifyStapleRole(card: ScryfallCard): Exclude<StapleRole, 'All'> {
  const text = (card.oracle_text || card.card_faces?.[0]?.oracle_text || '').toLowerCase();
  const type = (card.type_line || '').toLowerCase();

  // Board Wipes
  if (
    /destroy all|exile all|all creatures get -\d+\/-\d+|each creature gets -\d+\/-\d+|return all .* to their owners' hands|deals \d+ damage to each creature/.test(
      text
    )
  ) {
    return 'Wipes';
  }

  // Ramp & Mana
  if (
    (/add \{/.test(text) && (type.includes('artifact') || type.includes('creature') || type.includes('enchantment'))) ||
    /search your library for a .*land card.*onto the battlefield|treasure token/.test(text)
  ) {
    return 'Ramp';
  }

  // Protection & Tutors
  if (
    /search your library for a card|hexproof|indestructible|protection from|phase out|can't be countered/.test(text) &&
    !/basic land/.test(text)
  ) {
    return 'Protection & Tutors';
  }

  // Targeted Removal & Countermagic
  if (
    /destroy target|exile target|counter target|deals \d+ damage to target|target creature gets -|target player sacrifices/.test(
      text
    )
  ) {
    return 'Removal';
  }

  // Card Draw & Advantage
  if (/draw a card|draw two cards|draw three cards|draw cards|draws a card|look at the top/.test(text)) {
    return 'Draw';
  }

  return 'Protection & Tutors';
}

// Module-level caches
const edhrecCache = new Map<string, any>();
const spellbookCache = new Map<string, { almostIncluded: ComboSuggestion[]; included: ActiveDeckCombo[] }>();
const scryfallBatchCache = new Map<string, ScryfallCard>();
const staplesCache = new Map<string, StapleSuggestion[]>();
let cachedCommunityDecks: DeckRecord[] | null = null;

async function batchFetchScryfallCards(names: string[]): Promise<Map<string, ScryfallCard>> {
  const result = new Map<string, ScryfallCard>();
  const missing: string[] = [];

  for (const name of names) {
    const clean = name.includes('//') ? name.split('//')[0].trim() : name.trim();
    const key = clean.toLowerCase();
    if (scryfallBatchCache.has(key)) {
      result.set(key, scryfallBatchCache.get(key)!);
    } else if (!missing.includes(clean)) {
      missing.push(clean);
    }
  }

  // Scryfall /cards/collection accepts up to 75 identifiers per call
  for (let i = 0; i < missing.length; i += 70) {
    const chunk = missing.slice(i, i + 70);
    try {
      const resp = await fetch('https://api.scryfall.com/cards/collection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          identifiers: chunk.map((n) => ({ name: n })),
        }),
      });
      if (resp.ok) {
        const json = await resp.json();
        const data: ScryfallCard[] = json?.data || [];
        for (const sc of data) {
          const fullLower = sc.name.toLowerCase().trim();
          scryfallBatchCache.set(fullLower, sc);
          result.set(fullLower, sc);
          if (sc.name.includes('//')) {
            const frontLower = sc.name.split('//')[0].toLowerCase().trim();
            scryfallBatchCache.set(frontLower, sc);
            result.set(frontLower, sc);
          }
        }
      }
    } catch (err) {
      console.warn('Scryfall batch collection fetch warning:', err);
    }
  }

  return result;
}

function SuggestedCardThumb({
  name,
  initialUrl,
  onHover,
  onLeave,
}: {
  name: string;
  initialUrl?: string;
  onHover: (name: string, url: string, e: React.MouseEvent) => void;
  onLeave: () => void;
}) {
  const resolvedUrl = useResolvedCardImage(name, initialUrl);
  return (
    <div
      className="relative w-9 h-12 rounded overflow-hidden bg-slate-900 border border-white/15 flex-shrink-0 cursor-pointer group"
      onMouseEnter={(e) => onHover(name, resolvedUrl, e)}
      onMouseMove={(e) => onHover(name, resolvedUrl, e)}
      onMouseLeave={onLeave}
    >
      {resolvedUrl ? (
        <img
          src={resolvedUrl}
          alt={name}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-200"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[8px] text-white/30 text-center p-0.5">
          MTG
        </div>
      )}
    </div>
  );
}

export function SuggestedCardsSection({
  deck,
  sideboard,
  onAddCard,
  onAddToSideboard,
}: SuggestedCardsSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const [staplesExpanded, setStaplesExpanded] = useState(true);

  const [activeSubTab, setActiveSubTab] = useState<'all' | 'combos' | 'cooccurrence' | 'active'>('all');
  const [selectedArchetype, setSelectedArchetype] = useState<string>('__auto__');
  const [focusCardName, setFocusCardName] = useState<string>('__all__');
  const [matchColorIdentity, setMatchColorIdentity] = useState<boolean>(true);
  const [expandedComboId, setExpandedComboId] = useState<string | null>(null);

  const [selectedStapleRole, setSelectedStapleRole] = useState<StapleRole>('All');

  const [comboSuggestions, setComboSuggestions] = useState<ComboSuggestion[]>([]);
  const [activeCombos, setActiveCombos] = useState<ActiveDeckCombo[]>([]);
  const [coOccurrenceSuggestions, setCoOccurrenceSuggestions] = useState<CoOccurrenceSuggestion[]>([]);
  const [stapleSuggestions, setStapleSuggestions] = useState<StapleSuggestion[]>([]);

  const [isLoadingCombos, setIsLoadingCombos] = useState(false);
  const [isLoadingSynergy, setIsLoadingSynergy] = useState(false);
  const [isLoadingStaples, setIsLoadingStaples] = useState(false);

  const [addingCardKey, setAddingCardKey] = useState<string | null>(null);
  const [addedCardKey, setAddedCardKey] = useState<string | null>(null);

  const [previewCard, setPreviewCard] = useState<{ name: string; url: string; x: number; y: number } | null>(null);

  const allDeckCards = useMemo(() => [...deck, ...sideboard], [deck, sideboard]);

  const commanders = useMemo(() => allDeckCards.filter((c) => c.isCommander), [allDeckCards]);

  const nonLandBuildCards = useMemo(
    () =>
      deck
        .filter((c) => !c.isLand && !c.isExtraCard)
        .sort((a, b) => b.quantity - a.quantity || b.cmc - a.cmc),
    [deck]
  );

  // Set of card names already in mainboard or sideboard (lowercase)
  const ownedCardNamesLower = useMemo(() => {
    const s = new Set<string>();
    for (const c of allDeckCards) {
      s.add(c.name.toLowerCase().trim());
      if (c.name.includes('//')) {
        s.add(c.name.split('//')[0].toLowerCase().trim());
      }
    }
    return s;
  }, [allDeckCards]);

  // Deck color identity (union of commanders if present, otherwise all cards in deck)
  const deckColorIdentity = useMemo(() => {
    const colors = new Set<string>();
    const sourceCards = commanders.length > 0 ? commanders : allDeckCards;
    for (const c of sourceCards) {
      const ci = c.colorIdentity && c.colorIdentity.length > 0 ? c.colorIdentity : c.colors || [];
      ci.forEach((col) => {
        const up = col.toUpperCase();
        if (['W', 'U', 'B', 'R', 'G'].includes(up)) colors.add(up);
      });
    }
    return colors;
  }, [allDeckCards, commanders]);

  // Auto-detect which archetypes are present in the current deck based on card oracle text & types
  const detectedArchetypes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const card of allDeckCards) {
      if (card.isLand) continue;
      const text = `${card.oracleText || ''} ${card.type || ''}`;
      for (const arch of ARCHETYPE_DEFINITIONS) {
        if (arch.detectPatterns.some((rx) => rx.test(text))) {
          counts.set(arch.id, (counts.get(arch.id) || 0) + card.quantity);
        }
      }
    }
    return Array.from(counts.entries())
      .filter(([, count]) => count >= 1)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);
  }, [allDeckCards]);

  useEffect(() => {
    if (focusCardName !== '__all__') {
      const stillExists = allDeckCards.some((c) => c.name === focusCardName);
      if (!stillExists) setFocusCardName('__all__');
    }
  }, [allDeckCards, focusCardName]);

  const buildSignature = useMemo(() => {
    const cmdNames = commanders.map((c) => c.name).sort().join('|');
    const mainNames = nonLandBuildCards
      .map((c) => `${c.name}:${c.quantity}`)
      .sort()
      .join('|');
    return `${cmdNames}__${mainNames}`;
  }, [commanders, nonLandBuildCards]);

  // Check if a card's color identity fits within the deck's color identity
  const fitsColorIdentity = useCallback(
    (cardColors: string[] | undefined) => {
      if (!matchColorIdentity || deckColorIdentity.size === 0) return true;
      if (!cardColors || cardColors.length === 0) return true;
      return cardColors.every((c) => deckColorIdentity.has(c.toUpperCase()));
    },
    [deckColorIdentity, matchColorIdentity]
  );

  // Fetch Synergy, Archetype, and Combo Suggestions
  const fetchSuggestions = useCallback(
    async (forceRefresh = false) => {
      if (nonLandBuildCards.length === 0 && commanders.length === 0 && selectedArchetype === '__auto__') {
        setComboSuggestions([]);
        setActiveCombos([]);
        setCoOccurrenceSuggestions([]);
        return;
      }

      // 1. Fetch Combo Piece Suggestions via Commander Spellbook find-my-combos
      const runSpellbookFetch = async () => {
        if (nonLandBuildCards.length === 0 && commanders.length === 0) {
          setComboSuggestions([]);
          setActiveCombos([]);
          return;
        }

        const cacheKey = `${buildSignature}__${matchColorIdentity ? Array.from(deckColorIdentity).sort().join('') : 'ANY'}`;
        if (!forceRefresh && spellbookCache.has(cacheKey)) {
          const cached = spellbookCache.get(cacheKey)!;
          setComboSuggestions(cached.almostIncluded);
          setActiveCombos(cached.included);
          return;
        }

        setIsLoadingCombos(true);
        try {
          const mainPayload = deck
            .filter((c) => !c.isCommander && !c.isExtraCard)
            .map((c) => ({
              card: c.name.includes('//') ? c.name.split('//')[0].trim() : c.name,
              quantity: c.quantity,
            }));
          const cmdPayload = commanders.map((c) => ({
            card: c.name.includes('//') ? c.name.split('//')[0].trim() : c.name,
            quantity: 1,
          }));

          const res = await fetch('https://backend.commanderspellbook.com/find-my-combos/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ main: mainPayload, commanders: cmdPayload }),
          });

          if (res.ok) {
            const data = await res.json();
            const rawIncluded: any[] = data?.results?.included || [];
            const rawAlmost: any[] = data?.results?.almostIncluded || [];

            const parsedIncluded: ActiveDeckCombo[] = rawIncluded.slice(0, 20).map((variant: any) => ({
              id: String(variant.id),
              cardNames: (variant.uses || []).map((u: any) => u?.card?.name).filter(Boolean),
              produces: (variant.produces || []).map((p: any) => p?.feature?.name).filter(Boolean),
              description: variant.description || '',
              prerequisites: variant.notablePrerequisites || variant.easyPrerequisites || '',
              popularity: Number(variant.popularity || 0),
            }));

            const byMissingCard = new Map<string, ComboSuggestion>();

            for (const variant of rawAlmost) {
              const uses: any[] = variant.uses || [];
              const missingPieces: any[] = [];
              const existingPieces: string[] = [];

              for (const u of uses) {
                const cName: string = u?.card?.name || '';
                if (!cName) continue;
                const cleanLower = cName.toLowerCase().trim();
                const frontLower = cName.includes('//') ? cName.split('//')[0].toLowerCase().trim() : cleanLower;
                if (ownedCardNamesLower.has(cleanLower) || ownedCardNamesLower.has(frontLower)) {
                  existingPieces.push(cName);
                } else {
                  missingPieces.push(u.card);
                }
              }

              if (missingPieces.length === 1 && existingPieces.length >= 1) {
                const missingCard = missingPieces[0];
                const identityStr = String(variant.identity || '').toUpperCase();

                if (matchColorIdentity && deckColorIdentity.size > 0 && identityStr) {
                  const chars = identityStr.split('').filter((ch) => ['W', 'U', 'B', 'R', 'G'].includes(ch));
                  const outsideIdentity = chars.some((ch) => !deckColorIdentity.has(ch));
                  if (outsideIdentity) continue;
                }

                const popularity = Number(variant.popularity || 0);
                const existingEntry = byMissingCard.get(missingCard.name);

                if (!existingEntry || popularity > existingEntry.popularity) {
                  const mergedPartners = existingEntry
                    ? Array.from(new Set([...existingEntry.deckPartnerNames, ...existingPieces]))
                    : Array.from(new Set(existingPieces));

                  const producesList: string[] = (variant.produces || [])
                    .map((p: any) => p?.feature?.name)
                    .filter(Boolean);

                  byMissingCard.set(missingCard.name, {
                    id: String(variant.id),
                    missingCardName: missingCard.name,
                    missingCardImageUrl:
                      missingCard.imageUriFrontNormal ||
                      missingCard.imageUriFrontLarge ||
                      missingCard.imageUriFrontSmall ||
                      undefined,
                    missingCardTypeLine: missingCard.typeLine || '',
                    deckPartnerNames: mergedPartners,
                    produces: producesList.slice(0, 3),
                    description: variant.description || '',
                    prerequisites: variant.notablePrerequisites || variant.easyPrerequisites || '',
                    popularity,
                    manaNeeded: variant.manaNeeded || '',
                    identity: identityStr,
                    priceUsd: variant.prices?.tcgplayer || undefined,
                  });
                } else if (existingEntry) {
                  existingEntry.deckPartnerNames = Array.from(
                    new Set([...existingEntry.deckPartnerNames, ...existingPieces])
                  );
                }
              }
            }

            const sortedAlmost = Array.from(byMissingCard.values()).sort((a, b) => b.popularity - a.popularity);

            spellbookCache.set(cacheKey, {
              almostIncluded: sortedAlmost.slice(0, 30),
              included: parsedIncluded,
            });
            setComboSuggestions(sortedAlmost.slice(0, 30));
            setActiveCombos(parsedIncluded);
          }
        } catch (err) {
          console.warn('Spellbook combo suggestion fetch error:', err);
        } finally {
          setIsLoadingCombos(false);
        }
      };

      // 2. Fetch Pure Synergy & Archetype Co-Occurrence Cards (Excluding Generic Staples!)
      const runSynergyFetch = async () => {
        setIsLoadingSynergy(true);
        try {
          const seedCards: { name: string; isCommander: boolean }[] = [];

          if (focusCardName !== '__all__') {
            const found = allDeckCards.find((c) => c.name === focusCardName);
            if (found) {
              seedCards.push({ name: found.name, isCommander: Boolean(found.isCommander) });
            }
          } else {
            for (const cmd of commanders.slice(0, 2)) {
              seedCards.push({ name: cmd.name, isCommander: true });
            }
            for (const c of nonLandBuildCards) {
              if (seedCards.length >= 5) break;
              if (!seedCards.some((s) => s.name === c.name)) {
                seedCards.push({ name: c.name, isCommander: false });
              }
            }
          }

          if (!cachedCommunityDecks) {
            try {
              cachedCommunityDecks = await deckService.getPublicDecks(50, 0);
            } catch {
              cachedCommunityDecks = [];
            }
          }

          const aggMap = new Map<
            string,
            {
              name: string;
              slug: string;
              numDecks: number;
              potentialDecks: number;
              maxInclusionRate: number;
              maxLift: number;
              pairedWith: Set<string>;
              category: string;
              archetypeLabel?: string;
              deckstrDecksCount: number;
            }
          >();

          // Determine which EDHREC archetype tag(s) to query alongside card co-occurrences
          const activeArchetypeDefs: ArchetypeDef[] = [];
          if (selectedArchetype !== '__auto__') {
            const foundArch = ARCHETYPE_DEFINITIONS.find((a) => a.id === selectedArchetype);
            if (foundArch) activeArchetypeDefs.push(foundArch);
          } else if (detectedArchetypes.length > 0) {
            // Auto-include top detected archetype from build
            const topDetected = ARCHETYPE_DEFINITIONS.find((a) => a.id === detectedArchetypes[0]);
            if (topDetected) activeArchetypeDefs.push(topDetected);
          }

          // A) Fetch Archetype Tag JSON from EDHREC if an archetype is selected or auto-detected
          await Promise.all(
            activeArchetypeDefs.map(async (archDef) => {
              const endpoint = `https://json.edhrec.com/pages/tags/${archDef.edhrecTag}.json`;
              let json = edhrecCache.get(endpoint);
              if (!json) {
                try {
                  const resp = await fetch(endpoint);
                  if (resp.ok) {
                    json = await resp.json();
                    edhrecCache.set(endpoint, json);
                  }
                } catch {
                  /* ignore */
                }
              }

              const cardlists: any[] = json?.container?.json_dict?.cardlists || [];
              for (const list of cardlists) {
                const tag: string = list.tag || '';
                if (tag === 'newcommanders' || tag === 'topcommanders' || tag === 'lands' || tag === 'manaartifacts') {
                  continue;
                }
                const views: any[] = list.cardviews || [];
                for (const cv of views.slice(0, 22)) {
                  const cName: string = cv?.name || '';
                  if (!cName) continue;
                  const lower = cName.toLowerCase().trim();
                  if (ownedCardNamesLower.has(lower)) continue;

                  const lift = Number(cv.lift || (cv.synergy ? 1 + Number(cv.synergy) * 4 : 1));
                  // Filter out generic staples so only true archetype synergy cards appear
                  if (isGenericStapleCard(cName, lift)) continue;

                  const numDecks = Number(cv.num_decks || 0);
                  const potentialDecks = Number(cv.potential_decks || 1);
                  const rate = potentialDecks > 0 ? Math.min(100, (numDecks / potentialDecks) * 100) : 0;

                  const existing = aggMap.get(cName);
                  const tagReason = `${archDef.icon} ${archDef.label}`;
                  if (!existing) {
                    aggMap.set(cName, {
                      name: cName,
                      slug: cv.slug || toEdhrecSlug(cName),
                      numDecks,
                      potentialDecks,
                      maxInclusionRate: rate,
                      maxLift: lift,
                      pairedWith: new Set([tagReason]),
                      category: `${archDef.icon} ${archDef.label} Synergy`,
                      archetypeLabel: archDef.label,
                      deckstrDecksCount: 0,
                    });
                  } else {
                    existing.pairedWith.add(tagReason);
                    existing.archetypeLabel = archDef.label;
                    if (lift > existing.maxLift) existing.maxLift = lift;
                  }
                }
              }
            })
          );

          // B) Fetch Card-Specific High-Lift Co-Occurrences from EDHREC (when in Auto mode or alongside Archetype)
          if (selectedArchetype === '__auto__' || focusCardName !== '__all__') {
            await Promise.all(
              seedCards.map(async (seed) => {
                const slug = toEdhrecSlug(seed.name);
                if (!slug) return;

                const endpoint = seed.isCommander
                  ? `https://json.edhrec.com/pages/commanders/${slug}.json`
                  : `https://json.edhrec.com/pages/cards/${slug}.json`;

                let json = edhrecCache.get(endpoint);
                if (!json) {
                  try {
                    const resp = await fetch(endpoint);
                    if (resp.ok) {
                      json = await resp.json();
                      edhrecCache.set(endpoint, json);
                    }
                  } catch {
                    /* ignore */
                  }
                }

                const cardlists: any[] = json?.container?.json_dict?.cardlists || [];
                for (const list of cardlists) {
                  const tag: string = list.tag || '';
                  // Skip commanders, generic lands, and generic mana rocks
                  if (
                    tag === 'newcommanders' ||
                    tag === 'topcommanders' ||
                    tag === 'lands' ||
                    tag === 'manaartifacts' ||
                    tag === 'utilitylands'
                  ) {
                    continue;
                  }

                  const views: any[] = list.cardviews || [];
                  const limit = tag === 'highliftcards' || tag === 'highsynergycards' ? 20 : 12;

                  for (const cv of views.slice(0, limit)) {
                    const cName: string = cv?.name || '';
                    if (!cName) continue;
                    const lower = cName.toLowerCase().trim();
                    if (ownedCardNamesLower.has(lower)) continue;

                    const numDecks = Number(cv.num_decks || 0);
                    const potentialDecks = Number(cv.potential_decks || 1);
                    const lift = Number(cv.lift || (cv.synergy ? 1 + Number(cv.synergy) * 4 : 1));
                    const rate = potentialDecks > 0 ? Math.min(100, (numDecks / potentialDecks) * 100) : 0;

                    // STRICT SYNERGY FILTER: Exclude generic low-lift staples!
                    if (isGenericStapleCard(cName, lift)) continue;

                    const existing = aggMap.get(cName);
                    if (!existing) {
                      aggMap.set(cName, {
                        name: cName,
                        slug: cv.slug || toEdhrecSlug(cName),
                        numDecks,
                        potentialDecks,
                        maxInclusionRate: rate,
                        maxLift: lift,
                        pairedWith: new Set([seed.name]),
                        category: lift >= 8 ? '🔥 Extreme Synergy' : '🔗 Mechanical Synergy',
                        deckstrDecksCount: 0,
                      });
                    } else {
                      existing.pairedWith.add(seed.name);
                      if (numDecks > existing.numDecks) {
                        existing.numDecks = numDecks;
                        existing.potentialDecks = potentialDecks;
                      }
                      if (rate > existing.maxInclusionRate) existing.maxInclusionRate = rate;
                      if (lift > existing.maxLift) existing.maxLift = lift;
                    }
                  }
                }
              })
            );
          }

          // C) Enrich with DECKSTR Community Deck Co-Occurrences
          if (cachedCommunityDecks && cachedCommunityDecks.length > 0 && seedCards.length > 0) {
            const buildCardNames = seedCards.map((s) => s.name);
            for (const pubDeck of cachedCommunityDecks) {
              const pubCardsObj = { ...(pubDeck.cards || {}) };
              const pubCardNames = Object.keys(pubCardsObj);
              const sharedSeeds = buildCardNames.filter((bn) => pubCardsObj[bn] > 0);

              if (sharedSeeds.length > 0) {
                for (const candidateName of pubCardNames) {
                  const lower = candidateName.toLowerCase().trim();
                  if (ownedCardNamesLower.has(lower)) continue;
                  if (GENERIC_STAPLE_NAMES.has(lower)) continue;

                  const existing = aggMap.get(candidateName);
                  if (existing) {
                    existing.deckstrDecksCount += 1;
                    sharedSeeds.forEach((s) => existing.pairedWith.add(s));
                  }
                }
              }
            }
          }

          // D) Pre-rank top 55 candidates, then batch-verify Color Identity & Archetype match via Scryfall /cards/collection
          const preRanked = Array.from(aggMap.values())
            .sort((a, b) => {
              // If user explicitly picked an archetype, prioritize cards matching that archetype first
              if (selectedArchetype !== '__auto__') {
                const aHasArch = a.archetypeLabel ? 1 : 0;
                const bHasArch = b.archetypeLabel ? 1 : 0;
                if (bHasArch !== aHasArch) return bHasArch - aHasArch;
              }
              if (b.pairedWith.size !== a.pairedWith.size) {
                return b.pairedWith.size - a.pairedWith.size;
              }
              const scoreA = Math.log10(Math.max(10, a.numDecks)) * Math.min(35, a.maxLift);
              const scoreB = Math.log10(Math.max(10, b.numDecks)) * Math.min(35, b.maxLift);
              return scoreB - scoreA;
            })
            .slice(0, 55);

          const scryfallMap = await batchFetchScryfallCards(preRanked.map((item) => item.name));

          const activeFilterArch =
            selectedArchetype !== '__auto__'
              ? ARCHETYPE_DEFINITIONS.find((a) => a.id === selectedArchetype)
              : undefined;

          const verifiedResults: CoOccurrenceSuggestion[] = [];
          for (const item of preRanked) {
            const cleanLower = (item.name.includes('//') ? item.name.split('//')[0] : item.name)
              .toLowerCase()
              .trim();
            const sc = scryfallMap.get(cleanLower) || scryfallMap.get(item.name.toLowerCase().trim());

            if (sc) {
              // Strict Color Identity check
              if (!fitsColorIdentity(sc.color_identity)) continue;
              // Skip basic lands or pure mana rocks that slipped through
              if (sc.type_line?.toLowerCase().includes('basic land')) continue;

              // If user selected a specific archetype, verify either it came from that archetype tag OR its oracle text matches
              if (activeFilterArch && !item.archetypeLabel) {
                const text = `${sc.oracle_text || ''} ${sc.type_line || ''}`;
                const matchesArch = activeFilterArch.detectPatterns.some((rx) => rx.test(text));
                if (!matchesArch) continue;
              }
            }

            verifiedResults.push({
              name: item.name,
              slug: item.slug,
              numDecks: item.numDecks,
              potentialDecks: item.potentialDecks,
              inclusionRate: item.maxInclusionRate,
              maxLift: item.maxLift,
              pairedWith: Array.from(item.pairedWith),
              category: item.category,
              archetypeLabel: item.archetypeLabel,
              deckstrDecksCount: item.deckstrDecksCount,
              imageUrl: sc ? getCardImageUrl(sc) : undefined,
              typeLine: sc?.type_line,
              manaCost: sc?.mana_cost || sc?.card_faces?.[0]?.mana_cost,
              oracleHint: sc?.oracle_text
                ? sc.oracle_text.split('\n')[0].slice(0, 90)
                : undefined,
            });
          }

          setCoOccurrenceSuggestions(verifiedResults.slice(0, 28));
        } catch (err) {
          console.warn('Co-occurrence synergy fetch error:', err);
        } finally {
          setIsLoadingSynergy(false);
        }
      };

      await Promise.all([runSpellbookFetch(), runSynergyFetch()]);
    },
    [
      allDeckCards,
      buildSignature,
      commanders,
      deck,
      deckColorIdentity,
      detectedArchetypes,
      fitsColorIdentity,
      focusCardName,
      matchColorIdentity,
      nonLandBuildCards,
      ownedCardNamesLower,
      selectedArchetype,
    ]
  );

  // 3. Fetch Dedicated Color Identity Staples (Separate from Synergy/Combos!)
  const fetchColorIdentityStaples = useCallback(async () => {
    const colorArr = ['W', 'U', 'B', 'R', 'G'].filter((c) => deckColorIdentity.has(c));
    const colorKey = colorArr.length > 0 ? colorArr.join('').toLowerCase() : 'c';

    let staplesList = staplesCache.get(colorKey);
    if (!staplesList) {
      setIsLoadingStaples(true);
      try {
        // Query Scryfall for top EDHREC-ranked non-land cards strictly within the deck's color identity
        const q = `id<=${colorKey} -t:land -t:basic f:commander order:edhrec`;
        const resp = await fetch(
          `https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}&unique=cards`,
          { headers: { Accept: 'application/json' } }
        );
        if (resp.ok) {
          const json = await resp.json();
          const rawCards: ScryfallCard[] = (json?.data || []).slice(0, 65);
          staplesList = rawCards.map((sc) => {
            const role = classifyStapleRole(sc);
            const firstLine = (sc.oracle_text || sc.card_faces?.[0]?.oracle_text || '').split('\n')[0] || '';
            return {
              name: sc.name,
              role,
              manaCost: sc.mana_cost || sc.card_faces?.[0]?.mana_cost || '',
              cmc: sc.cmc,
              typeLine: sc.type_line || '',
              colorIdentity: sc.color_identity || [],
              imageUrl: getCardImageUrl(sc),
              oracleSummary: firstLine.length > 85 ? `${firstLine.slice(0, 82)}...` : firstLine,
            };
          });
          staplesCache.set(colorKey, staplesList);
        }
      } catch (err) {
        console.warn('Failed to fetch color identity staples:', err);
      } finally {
        setIsLoadingStaples(false);
      }
    }

    if (staplesList) {
      // Filter out cards already in the deck
      setStapleSuggestions(
        staplesList.filter((st) => !ownedCardNamesLower.has(st.name.toLowerCase().trim()))
      );
    }
  }, [deckColorIdentity, ownedCardNamesLower]);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(false);
      fetchColorIdentityStaples();
    }, 550);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [fetchSuggestions, fetchColorIdentityStaples]);

  // Quick-add a suggested card by name to Mainboard or Sideboard
  const handleQuickAddCard = async (cardName: string, toSideboard: boolean) => {
    if (!onAddCard && !onAddToSideboard) return;
    const key = `${cardName}-${toSideboard ? 'sb' : 'mb'}`;
    setAddingCardKey(key);

    try {
      const data = await fetchCardData(cardName, 'fuzzy');
      const rarityMap: Record<string, 'C' | 'U' | 'R' | 'M' | 'B'> = {
        common: 'C',
        uncommon: 'U',
        rare: 'R',
        mythic: 'M',
        special: 'R',
        bonus: 'R',
      };
      const fullTypeLine = data.type_line || '';
      const frontFaceTypeLine = data.card_faces?.[0]?.type_line ?? fullTypeLine;
      const isLand = frontFaceTypeLine.toLowerCase().includes('land');
      const isBasicLand = frontFaceTypeLine.toLowerCase().includes('basic land');

      const newCard: Card = {
        id: `${data.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: data.name,
        quantity: 1,
        type: fullTypeLine,
        manaCost: data.mana_cost || data.card_faces?.[0]?.mana_cost || '',
        cmc: data.cmc,
        colors: data.colors || data.card_faces?.[0]?.colors || [],
        colorIdentity: data.color_identity || [],
        imageUrl: getCardImageUrl(data),
        scryfallId: data.id,
        isLand,
        rarity: isBasicLand ? 'B' : rarityMap[data.rarity] || 'C',
        oracleText: data.oracle_text || data.card_faces?.[0]?.oracle_text,
        legalities: data.legalities,
        setName: data.set_name,
        setCode: data.set,
        collectorNumber: data.collector_number,
        prices: data.prices,
        purchaseUris: data.purchase_uris,
        scryfallUri: data.scryfall_uri,
      };

      if (toSideboard && onAddToSideboard) {
        onAddToSideboard(newCard);
      } else if (onAddCard) {
        onAddCard(newCard);
      }

      setAddedCardKey(key);
      setTimeout(() => {
        setAddedCardKey((prev) => (prev === key ? null : prev));
      }, 1400);
    } catch (err) {
      console.error(`Failed to quick-add suggested card "${cardName}":`, err);
    } finally {
      setAddingCardKey(null);
    }
  };

  // Filter combo suggestions by Focus Card AND by selected Archetype
  const filteredComboSuggestions = useMemo(() => {
    let list = comboSuggestions;
    if (focusCardName !== '__all__') {
      const focusLower = focusCardName.toLowerCase();
      list = list.filter((cs) =>
        cs.deckPartnerNames.some((p) => p.toLowerCase().includes(focusLower) || focusLower.includes(p.toLowerCase()))
      );
    }
    if (selectedArchetype !== '__auto__') {
      const archDef = ARCHETYPE_DEFINITIONS.find((a) => a.id === selectedArchetype);
      if (archDef) {
        const archMatches = list.filter((cs) => {
          const text = `${cs.produces.join(' ')} ${cs.description} ${cs.prerequisites}`;
          return archDef.detectPatterns.some((rx) => rx.test(text));
        });
        // If there are combos matching the selected archetype, show them; otherwise keep combos visible
        if (archMatches.length > 0) list = archMatches;
      }
    }
    return list;
  }, [comboSuggestions, focusCardName, selectedArchetype]);

  const filteredStaples = useMemo(() => {
    if (selectedStapleRole === 'All') return stapleSuggestions.slice(0, 20);
    return stapleSuggestions.filter((s) => s.role === selectedStapleRole).slice(0, 20);
  }, [selectedStapleRole, stapleSuggestions]);

  const handleThumbHover = (name: string, url: string, e: React.MouseEvent) => {
    if (!url) return;
    setPreviewCard({ name, url, x: e.clientX, y: e.clientY });
  };

  const handleThumbLeave = () => {
    setPreviewCard(null);
  };

  const totalSuggestionCount = filteredComboSuggestions.length + coOccurrenceSuggestions.length;
  const isLoading = isLoadingCombos || isLoadingSynergy;
  const colorIdentityLabel = getColorIdentityName(deckColorIdentity);

  return (
    <div className="mt-4 space-y-4">
      {/* ===================================================================== */}
      {/* PANEL 1: PURE SYNERGY, ARCHETYPE & COMBO PIECE SUGGESTER (NO STAPLES) */}
      {/* ===================================================================== */}
      <div className="rounded-lg border border-amber-500/25 bg-slate-950/50 overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-amber-500/20 via-purple-500/15 to-cyan-500/15 hover:from-amber-500/30 hover:via-purple-500/25 hover:to-cyan-500/25 transition-colors"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-amber-300 font-semibold tracking-wide">
              SUGGESTED — Combo Pieces & Archetype Synergies
            </span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 font-medium">
              Staples Filtered Out
            </span>
            {filteredComboSuggestions.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold">
                {filteredComboSuggestions.length} Combo {filteredComboSuggestions.length === 1 ? 'Piece' : 'Pieces'}
              </span>
            )}
            {coOccurrenceSuggestions.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-semibold">
                {coOccurrenceSuggestions.length} High-Synergy
              </span>
            )}
            {activeCombos.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-semibold">
                {activeCombos.length} Active {activeCombos.length === 1 ? 'Combo' : 'Combos'} in Deck
              </span>
            )}
            {isLoading && (
              <span className="text-[10px] text-purple-300/80 flex items-center gap-1">
                <RefreshCw className="size-3 animate-spin" /> Scanning synergies...
              </span>
            )}
          </div>
          <span className={`text-purple-400 text-xs transition-transform ${expanded ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {expanded && (
          <div className="p-3 space-y-3">
            {/* ARCHETYPE FILTER STRIP (Lifegain, Mill, Group Slug, Crime, Control, Group Hug, etc.) */}
            <div className="bg-slate-900/70 rounded-lg p-2 border border-white/10 space-y-1.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-white/85 uppercase tracking-wider">
                    Archetype & Strategy Filter
                  </span>
                  {detectedArchetypes.length > 0 && (
                    <span className="text-[10px] text-amber-300/80">
                      (Detected in build:{' '}
                      {detectedArchetypes
                        .slice(0, 3)
                        .map((id) => ARCHETYPE_DEFINITIONS.find((a) => a.id === id)?.label)
                        .filter(Boolean)
                        .join(', ')}
                      )
                    </span>
                  )}
                </div>
                {selectedArchetype !== '__auto__' && (
                  <button
                    type="button"
                    onClick={() => setSelectedArchetype('__auto__')}
                    className="text-[10px] text-amber-300 hover:text-amber-200 underline"
                  >
                    Reset to Auto-Detect
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
                <BrewTooltip
                  title="Auto-Detect Build Synergies"
                  badge="Smart Match"
                  description="Automatically suggests combo pieces and high-lift synergy cards based on the exact cards and mechanics already in your deck."
                  accent="amber"
                >
                  <button
                    type="button"
                    onClick={() => setSelectedArchetype('__auto__')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap border transition-all flex items-center gap-1 ${
                      selectedArchetype === '__auto__'
                        ? 'bg-amber-500/25 text-amber-200 border-amber-400/50 shadow-sm'
                        : 'bg-white/5 text-white/55 border-white/10 hover:bg-white/10 hover:text-white/85'
                    }`}
                  >
                    <span>Auto (From Build)</span>
                  </button>
                </BrewTooltip>

                {ARCHETYPE_DEFINITIONS.map((arch) => {
                  const isSelected = selectedArchetype === arch.id;
                  const isDetected = detectedArchetypes.includes(arch.id);

                  return (
                    <BrewTooltip
                      key={arch.id}
                      title={`${arch.label} Archetype`}
                      badge={isDetected ? 'Detected in Deck' : 'Archetype Filter'}
                      description={arch.description}
                      accent={isDetected ? 'amber' : 'cyan'}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedArchetype(isSelected ? '__auto__' : arch.id)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap border transition-all flex items-center gap-1.5 ${
                          isSelected
                            ? 'bg-purple-500/35 text-purple-100 border-purple-400/60 shadow-sm'
                            : isDetected
                            ? 'bg-amber-500/15 text-amber-200 border-amber-500/35 hover:bg-amber-500/25'
                            : 'bg-white/5 text-white/55 border-white/10 hover:bg-white/10 hover:text-white/85'
                        }`}
                      >
                        <span>{arch.label}</span>
                        {isDetected && (
                          <span className="size-1.5 rounded-full bg-amber-400 animate-pulse" />
                        )}
                      </button>
                    </BrewTooltip>
                  );
                })}
              </div>
            </div>

            {nonLandBuildCards.length === 0 && commanders.length === 0 && selectedArchetype === '__auto__' ? (
              <div className="py-5 px-4 text-center rounded-lg bg-white/[0.02] border border-dashed border-white/10">
                <p className="text-xs text-white/75 font-medium">
                  Add a Commander or non-land cards to your deck — or click any Archetype above (like{' '}
                  <span className="text-amber-300">Lifegain</span>, <span className="text-cyan-300">Mill</span>,{' '}
                  <span className="text-purple-300">Crime</span>, or <span className="text-red-300">Group Slug</span>)
                  to browse high-synergy cards!
                </p>
              </div>
            ) : (
              <>
                {/* Sub-Tabs + Seed Card Selector + Color Identity Toggle */}
                <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-white/10">
                  <div className="flex items-center gap-1 bg-slate-900/90 p-0.5 rounded-lg border border-white/10">
                    <BrewTooltip
                      title="All Synergy Recommendations"
                      badge={`${totalSuggestionCount} Cards`}
                      description="View both 1-card-away combo pieces and high-lift archetype co-occurrence cards together."
                      accent="amber"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveSubTab('all')}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                          activeSubTab === 'all'
                            ? 'bg-amber-500/25 text-amber-300 border border-amber-500/40'
                            : 'text-white/50 hover:text-white/80'
                        }`}
                      >
                        All Synergy ({totalSuggestionCount})
                      </button>
                    </BrewTooltip>

                    <BrewTooltip
                      title="1-Piece-Away Combo Completers"
                      badge={`${filteredComboSuggestions.length} Combos`}
                      description="Cards that complete an immediate Commander Spellbook combo with cards already in your deck."
                      accent="purple"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveSubTab('combos')}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                          activeSubTab === 'combos'
                            ? 'bg-purple-500/30 text-purple-200 border border-purple-500/40'
                            : 'text-white/50 hover:text-white/80'
                        }`}
                      >
                        Combo Pieces ({filteredComboSuggestions.length})
                      </button>
                    </BrewTooltip>

                    <BrewTooltip
                      title="High-Lift Synergy Co-Occurrences"
                      badge={`${coOccurrenceSuggestions.length} Cards`}
                      description="Non-staple synergy cards run disproportionately often alongside your deck's cards and archetype."
                      accent="cyan"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveSubTab('cooccurrence')}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                          activeSubTab === 'cooccurrence'
                            ? 'bg-cyan-500/25 text-cyan-200 border border-cyan-500/40'
                            : 'text-white/50 hover:text-white/80'
                        }`}
                      >
                        High-Lift Synergy ({coOccurrenceSuggestions.length})
                      </button>
                    </BrewTooltip>

                    <BrewTooltip
                      title="Active Combos Already in Deck"
                      badge={`${activeCombos.length} Complete`}
                      description="Complete multi-card combos whose pieces are already present inside your current decklist."
                      accent="green"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveSubTab('active')}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                          activeSubTab === 'active'
                            ? 'bg-emerald-500/25 text-emerald-200 border border-emerald-500/40'
                            : 'text-white/50 hover:text-white/80'
                        }`}
                      >
                        In Deck ({activeCombos.length})
                      </button>
                    </BrewTooltip>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <BrewTooltip
                      title="Focus on Specific Deck Card"
                      badge="Seed Filter"
                      description="Filter combo pieces and synergy co-occurrence stats to a single card in your deck, or analyze your entire build."
                      accent="cyan"
                    >
                      <div className="flex items-center gap-1 bg-slate-900/90 px-2 py-1 rounded-md border border-white/10">
                        <Filter className="size-3 text-white/40" />
                        <span className="text-[10px] text-white/45">Seed:</span>
                        <select
                          value={focusCardName}
                          onChange={(e) => setFocusCardName(e.target.value)}
                          className="bg-transparent text-[11px] text-amber-300 font-medium focus:outline-none max-w-[155px] truncate cursor-pointer"
                        >
                          <option value="__all__" className="bg-slate-900 text-white">
                            Entire Build ({nonLandBuildCards.length + commanders.length} cards)
                          </option>
                          {commanders.length > 0 && (
                            <optgroup label="Commander" className="bg-slate-900 text-amber-300">
                              {commanders.map((c) => (
                                <option key={c.id} value={c.name} className="bg-slate-900 text-white">
                                  {c.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Mainboard Cards" className="bg-slate-900 text-purple-300">
                            {nonLandBuildCards.map((c) => (
                              <option key={c.id} value={c.name} className="bg-slate-900 text-white">
                                {c.name} ({c.quantity}×)
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    </BrewTooltip>

                    <BrewTooltip
                      title="Enforce Deck Color Identity"
                      badge={matchColorIdentity ? colorIdentityLabel : 'All Colors'}
                      description="When enabled, strictly filters both combo pieces and archetype synergy cards to fit your deck's color identity."
                      accent="purple"
                    >
                      <button
                        type="button"
                        onClick={() => setMatchColorIdentity(!matchColorIdentity)}
                        className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-colors ${
                          matchColorIdentity
                            ? 'bg-purple-500/20 text-purple-300 border-purple-500/35'
                            : 'bg-white/5 text-white/40 border-white/10 hover:text-white/70'
                        }`}
                      >
                        {matchColorIdentity ? colorIdentityLabel : 'Any Color'}
                      </button>
                    </BrewTooltip>

                    <BrewTooltip
                      title="Re-Scan Deck Synergies"
                      description="Force a fresh scan of Commander Spellbook combos and EDHREC archetype co-occurrences."
                      accent="amber"
                    >
                      <button
                        type="button"
                        onClick={() => fetchSuggestions(true)}
                        disabled={isLoading}
                        className="p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 transition-colors disabled:opacity-40"
                      >
                        <RefreshCw className={`size-3.5 ${isLoading ? 'animate-spin text-amber-400' : ''}`} />
                      </button>
                    </BrewTooltip>
                  </div>
                </div>

                {/* SECTION A: 1-Piece-Away Combo Completers */}
                {(activeSubTab === 'all' || activeSubTab === 'combos') && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
                        Combo Piece Suggester — 1 Card Away ({filteredComboSuggestions.length})
                      </h5>
                      <span className="text-[10px] text-white/40">
                        Ranked by decks running the combo together
                      </span>
                    </div>

                    {isLoadingCombos && filteredComboSuggestions.length === 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {[1, 2, 3].map((n) => (
                          <div
                            key={n}
                            className="h-20 rounded-lg bg-white/[0.03] border border-white/5 animate-pulse p-2.5"
                          />
                        ))}
                      </div>
                    ) : filteredComboSuggestions.length === 0 ? (
                      <div className="py-3 px-3 rounded-lg bg-white/[0.02] border border-white/5 text-xs text-white/45">
                        No 1-card missing combos found for the current filter.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                        {filteredComboSuggestions
                          .slice(0, activeSubTab === 'all' ? 6 : 18)
                          .map((combo) => {
                            const isComboOpen = expandedComboId === combo.id;
                            const mbKey = `${combo.missingCardName}-mb`;
                            const sbKey = `${combo.missingCardName}-sb`;

                            return (
                              <div
                                key={`${combo.id}-${combo.missingCardName}`}
                                className="rounded-lg bg-gradient-to-br from-purple-950/40 via-slate-900/80 to-amber-950/25 border border-amber-500/25 hover:border-amber-400/45 p-2.5 flex flex-col justify-between transition-all"
                              >
                                <div className="flex items-start gap-2.5">
                                  <SuggestedCardThumb
                                    name={combo.missingCardName}
                                    initialUrl={combo.missingCardImageUrl}
                                    onHover={handleThumbHover}
                                    onLeave={handleThumbLeave}
                                  />

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-1">
                                      <span
                                        className="text-xs font-bold text-white truncate cursor-pointer hover:text-amber-300 transition-colors"
                                        onMouseEnter={(e) =>
                                          combo.missingCardImageUrl &&
                                          handleThumbHover(combo.missingCardName, combo.missingCardImageUrl, e)
                                        }
                                        onMouseLeave={handleThumbLeave}
                                      >
                                        {combo.missingCardName}
                                      </span>

                                      <BrewTooltip
                                        title="Combo Co-Occurrence Popularity"
                                        badge={`${combo.popularity.toLocaleString()} Decks`}
                                        description={`${combo.popularity.toLocaleString()} tracked decks run ${combo.missingCardName} alongside ${combo.deckPartnerNames.join(' + ')}.`}
                                        accent="amber"
                                      >
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold whitespace-nowrap flex-shrink-0">
                                          {formatCompactNumber(combo.popularity)} decks
                                        </span>
                                      </BrewTooltip>
                                    </div>

                                    <div className="mt-1 flex items-center gap-1 flex-wrap">
                                      <span className="text-[10px] text-white/45">Combos with your:</span>
                                      {combo.deckPartnerNames.map((partner) => (
                                        <span
                                          key={partner}
                                          className="text-[10px] px-1.5 py-0.2 rounded bg-purple-500/25 text-purple-200 border border-purple-400/30 font-medium truncate max-w-[160px]"
                                        >
                                          + {partner}
                                        </span>
                                      ))}
                                    </div>

                                    {combo.produces.length > 0 && (
                                      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                                        {combo.produces.map((prod) => (
                                          <span
                                            key={prod}
                                            className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                                          >
                                            {prod}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-2 pt-1.5 border-t border-white/10 flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedComboId(isComboOpen ? null : combo.id)}
                                    className="text-[10px] text-purple-300/80 hover:text-purple-200 flex items-center gap-1 transition-colors"
                                  >
                                    <Info className="size-3" />
                                    <span>{isComboOpen ? 'Hide combo steps' : 'How combo works'}</span>
                                    {isComboOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                                  </button>

                                  <div className="flex items-center gap-1">
                                    {onAddCard && (
                                      <BrewTooltip
                                        title="Add to Main Deck (+MAIN)"
                                        description={`Add 1 copy of ${combo.missingCardName} to your Main Deck.`}
                                        accent="purple"
                                      >
                                        <button
                                          type="button"
                                          disabled={addingCardKey === mbKey}
                                          onClick={() => handleQuickAddCard(combo.missingCardName, false)}
                                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-600/80 hover:bg-purple-500 text-white border border-purple-400/40 flex items-center gap-0.5 transition-colors disabled:opacity-50"
                                        >
                                          <Plus className="size-2.5" />
                                          {addedCardKey === mbKey ? 'Added!' : addingCardKey === mbKey ? '...' : 'MAIN'}
                                        </button>
                                      </BrewTooltip>
                                    )}
                                    {onAddToSideboard && (
                                      <BrewTooltip
                                        title="Add to Sideboard (+SIDE)"
                                        description={`Add 1 copy of ${combo.missingCardName} to your Sideboard / Command zone.`}
                                        accent="amber"
                                      >
                                        <button
                                          type="button"
                                          disabled={addingCardKey === sbKey}
                                          onClick={() => handleQuickAddCard(combo.missingCardName, true)}
                                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-600/75 hover:bg-amber-500 text-white border border-amber-400/40 transition-colors disabled:opacity-50"
                                        >
                                          {addedCardKey === sbKey ? '✓' : '+SIDE'}
                                        </button>
                                      </BrewTooltip>
                                    )}
                                  </div>
                                </div>

                                {isComboOpen && (
                                  <div className="mt-2 p-2 rounded bg-black/50 border border-purple-500/25 text-[10px] text-white/80 space-y-1">
                                    {combo.prerequisites && (
                                      <div className="text-amber-300/90">
                                        <span className="font-semibold">Setup: </span>
                                        {combo.prerequisites}
                                      </div>
                                    )}
                                    <div className="whitespace-pre-line text-white/75 leading-relaxed">
                                      {combo.description}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* SECTION B: High-Lift Mechanical Synergy & Archetype Co-Occurrences */}
                {(activeSubTab === 'all' || activeSubTab === 'cooccurrence') && (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">
                        {selectedArchetype !== '__auto__'
                          ? `${ARCHETYPE_DEFINITIONS.find((a) => a.id === selectedArchetype)?.label || ''} Archetype & Build Synergies (${coOccurrenceSuggestions.length})`
                          : `High-Lift Synergy Cards Run Together (${coOccurrenceSuggestions.length})`}
                      </h5>
                      <span className="text-[10px] text-white/40">
                        Generic staples excluded • Minimum 2.0× synergy lift
                      </span>
                    </div>

                    {isLoadingSynergy && coOccurrenceSuggestions.length === 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {[1, 2, 3, 4].map((n) => (
                          <div
                            key={n}
                            className="h-16 rounded-lg bg-white/[0.03] border border-white/5 animate-pulse p-2"
                          />
                        ))}
                      </div>
                    ) : coOccurrenceSuggestions.length === 0 ? (
                      <div className="py-3 px-3 rounded-lg bg-white/[0.02] border border-white/5 text-xs text-white/45">
                        No high-lift synergy cards found for this filter. Try switching the Archetype filter above or toggling{' '}
                        <span className="text-purple-300">Any Color</span>.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                        {coOccurrenceSuggestions
                          .slice(0, activeSubTab === 'all' ? 8 : 24)
                          .map((item) => {
                            const mbKey = `${item.name}-mb`;
                            const sbKey = `${item.name}-sb`;
                            const pctRounded = Math.round(item.inclusionRate * 10) / 10;

                            return (
                              <div
                                key={item.name}
                                className="rounded-lg bg-slate-900/75 hover:bg-slate-900 border border-white/10 hover:border-cyan-500/35 p-2 flex flex-col justify-between transition-all"
                              >
                                <div className="flex items-start gap-2">
                                  <SuggestedCardThumb
                                    name={item.name}
                                    initialUrl={item.imageUrl}
                                    onHover={handleThumbHover}
                                    onLeave={handleThumbLeave}
                                  />

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-1">
                                      <span className="text-xs font-semibold text-white truncate">{item.name}</span>
                                      <BrewTooltip
                                        title="Mechanical Synergy Lift"
                                        badge={`${item.maxLift.toFixed(1)}× Lift`}
                                        description={`Decks with ${item.pairedWith.join(', ')} are ${item.maxLift.toFixed(1)}× more likely to run ${item.name} than the average deck.`}
                                        accent="cyan"
                                      >
                                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-semibold whitespace-nowrap">
                                          {item.maxLift.toFixed(1)}× synergy
                                        </span>
                                      </BrewTooltip>
                                    </div>

                                    <BrewTooltip
                                      title="Co-Occurrence Deck Statistics"
                                      badge={`${pctRounded}% Inclusion`}
                                      description={`Run together in ${item.numDecks.toLocaleString()} out of ${item.potentialDecks.toLocaleString()} eligible decks (${pctRounded}%).`}
                                      accent="purple"
                                    >
                                      <div className="mt-1">
                                        <div className="flex items-center justify-between text-[10px] text-white/65 mb-0.5">
                                          <span className="font-semibold text-amber-300">{pctRounded}% of decks</span>
                                          <span className="text-white/45">
                                            {formatCompactNumber(item.numDecks)} /{' '}
                                            {formatCompactNumber(item.potentialDecks)}
                                          </span>
                                        </div>
                                        <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                                          <div
                                            className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-purple-400"
                                            style={{ width: `${Math.max(4, Math.min(100, pctRounded))}%` }}
                                          />
                                        </div>
                                      </div>
                                    </BrewTooltip>

                                    <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                                      <span className="text-[9px] text-white/40">Synergy:</span>
                                      {item.pairedWith.slice(0, 2).map((seed) => (
                                        <span
                                          key={seed}
                                          className="text-[9px] px-1 py-0.2 rounded bg-white/5 text-white/75 border border-white/10 truncate max-w-[125px]"
                                        >
                                          {seed}
                                        </span>
                                      ))}
                                      {item.pairedWith.length > 2 && (
                                        <span className="text-[9px] text-cyan-300">
                                          +{item.pairedWith.length - 2} more
                                        </span>
                                      )}
                                      {item.deckstrDecksCount > 0 && (
                                        <span className="text-[9px] px-1 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                          {item.deckstrDecksCount} DECKSTR
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-2 pt-1.5 border-t border-white/5 flex items-center justify-between">
                                  <span className="text-[9px] text-white/40 truncate max-w-[130px]">
                                    {item.category}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {onAddCard && (
                                      <BrewTooltip
                                        title="Add to Main Deck (+MAIN)"
                                        description={`Add 1 copy of ${item.name} to your Main Deck.`}
                                        accent="purple"
                                      >
                                        <button
                                          type="button"
                                          disabled={addingCardKey === mbKey}
                                          onClick={() => handleQuickAddCard(item.name, false)}
                                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-600/80 hover:bg-purple-500 text-white border border-purple-400/40 flex items-center gap-0.5 transition-colors"
                                        >
                                          <Plus className="size-2.5" />
                                          {addedCardKey === mbKey ? 'Added!' : addingCardKey === mbKey ? '...' : 'MAIN'}
                                        </button>
                                      </BrewTooltip>
                                    )}
                                    {onAddToSideboard && (
                                      <BrewTooltip
                                        title="Add to Sideboard (+SIDE)"
                                        description={`Add 1 copy of ${item.name} to your Sideboard / Command zone.`}
                                        accent="amber"
                                      >
                                        <button
                                          type="button"
                                          disabled={addingCardKey === sbKey}
                                          onClick={() => handleQuickAddCard(item.name, true)}
                                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-600/75 hover:bg-amber-500 text-white border border-amber-400/40 transition-colors"
                                        >
                                          {addedCardKey === sbKey ? '✓' : '+SIDE'}
                                        </button>
                                      </BrewTooltip>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* SECTION C: Active Combos Already Completed in Your Deck */}
                {(activeSubTab === 'active' || (activeSubTab === 'all' && activeCombos.length > 0)) && (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">
                        Active Combos Already in Your Deck ({activeCombos.length})
                      </h5>
                    </div>

                    {activeCombos.length === 0 ? (
                      <div className="py-3 px-3 rounded-lg bg-white/[0.02] border border-white/5 text-xs text-white/45">
                        No complete combos detected inside your current decklist yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {activeCombos.map((ac) => (
                          <div
                            key={ac.id}
                            className="rounded-lg bg-emerald-950/20 border border-emerald-500/30 p-2.5 space-y-1.5"
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-1 flex-wrap">
                                {ac.cardNames.map((cn, idx) => (
                                  <React.Fragment key={cn}>
                                    {idx > 0 && <span className="text-emerald-400 text-xs font-bold">+</span>}
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-500/30">
                                      {cn}
                                    </span>
                                  </React.Fragment>
                                ))}
                              </div>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold">
                                {formatCompactNumber(ac.popularity)} decks
                              </span>
                            </div>
                            {ac.produces.length > 0 && (
                              <div className="flex items-center gap-1 flex-wrap">
                                {ac.produces.map((p) => (
                                  <span
                                    key={p}
                                    className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-200 border border-purple-500/30"
                                  >
                                    {p}
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="text-[10px] text-white/70 whitespace-pre-line leading-relaxed">
                              {ac.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ===================================================================== */}
      {/* PANEL 2: SEPARATE SECTION FOR STAPLES IN THE DECK'S COLOR IDENTITY    */}
      {/* ===================================================================== */}
      <div className="rounded-lg border border-purple-500/25 bg-slate-950/50 overflow-hidden">
        <button
          type="button"
          onClick={() => setStaplesExpanded(!staplesExpanded)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gradient-to-r from-purple-500/20 via-indigo-500/15 to-slate-500/15 hover:from-purple-500/30 hover:via-indigo-500/25 transition-colors"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-purple-200 font-semibold tracking-wide">
              COLOR IDENTITY STAPLES — {colorIdentityLabel}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
              {stapleSuggestions.length} Utility Staples
            </span>
            {isLoadingStaples && (
              <span className="text-[10px] text-purple-300/80 flex items-center gap-1">
                <RefreshCw className="size-3 animate-spin" /> Loading color staples...
              </span>
            )}
          </div>
          <span className={`text-purple-400 text-xs transition-transform ${staplesExpanded ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {staplesExpanded && (
          <div className="p-3 space-y-3">
            {/* Staple Role Filter Bar */}
            <div className="flex items-center justify-between gap-2 flex-wrap pb-2 border-b border-white/10">
              <div className="flex items-center gap-1 flex-wrap">
                {(
                  [
                    {
                      role: 'All',
                      label: 'All Staples',
                      desc: 'Show all recommended format staples legal within your color identity.',
                    },
                    {
                      role: 'Removal',
                      label: 'Removal & Counter',
                      desc: 'Targeted creature/permanent removal and stack interaction.',
                    },
                    {
                      role: 'Ramp',
                      label: 'Ramp & Mana',
                      desc: 'Mana rocks, dorks, and land acceleration staples.',
                    },
                    {
                      role: 'Draw',
                      label: 'Card Draw',
                      desc: 'Card advantage engines and draw spells in your colors.',
                    },
                    {
                      role: 'Wipes',
                      label: 'Board Wipes',
                      desc: 'Mass sweepers and board resets legal in your color identity.',
                    },
                    {
                      role: 'Protection & Tutors',
                      label: 'Protection & Tutors',
                      desc: 'Board/spell protection pieces and library tutors.',
                    },
                  ] as { role: StapleRole; label: string; desc: string }[]
                ).map((tab) => (
                  <BrewTooltip
                    key={tab.role}
                    title={tab.label}
                    badge={selectedStapleRole === tab.role ? 'Active' : colorIdentityLabel}
                    description={tab.desc}
                    accent="purple"
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedStapleRole(tab.role)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                        selectedStapleRole === tab.role
                          ? 'bg-purple-500/30 text-purple-200 border-purple-400/50'
                          : 'bg-white/5 text-white/50 border-white/10 hover:text-white/80'
                      }`}
                    >
                      {tab.label}
                    </button>
                  </BrewTooltip>
                ))}
              </div>

              <span className="text-[10px] text-white/40">
                Kept separate so staples never clutter your combo & synergy recommendations
              </span>
            </div>

            {isLoadingStaples && filteredStaples.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="h-14 rounded-lg bg-white/[0.03] border border-white/5 animate-pulse p-2" />
                ))}
              </div>
            ) : filteredStaples.length === 0 ? (
              <div className="py-3 px-3 rounded-lg bg-white/[0.02] border border-white/5 text-xs text-white/45">
                No staples found for the <span className="text-purple-300">{selectedStapleRole}</span> category in{' '}
                {colorIdentityLabel}.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {filteredStaples.map((staple) => {
                  const mbKey = `${staple.name}-mb`;
                  const sbKey = `${staple.name}-sb`;

                  return (
                    <div
                      key={staple.name}
                      className="rounded-lg bg-slate-900/70 hover:bg-slate-900 border border-white/10 hover:border-purple-500/35 p-2 flex flex-col justify-between transition-all"
                    >
                      <div className="flex items-start gap-2">
                        <SuggestedCardThumb
                          name={staple.name}
                          initialUrl={staple.imageUrl}
                          onHover={handleThumbHover}
                          onLeave={handleThumbLeave}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-semibold text-white truncate">{staple.name}</span>
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 whitespace-nowrap">
                              {staple.role}
                            </span>
                          </div>
                          <div className="text-[10px] text-white/45 truncate mt-0.5">{staple.typeLine}</div>
                          {staple.oracleSummary && (
                            <p className="text-[9px] text-white/55 line-clamp-2 mt-0.5 leading-snug">
                              {staple.oracleSummary}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 pt-1 border-t border-white/5 flex items-center justify-between">
                        <span className="text-[9px] text-white/35">MV {staple.cmc}</span>
                        <div className="flex items-center gap-1">
                          {onAddCard && (
                            <BrewTooltip
                              title="Add to Main Deck (+MAIN)"
                              description={`Add 1 copy of ${staple.name} to your Main Deck.`}
                              accent="purple"
                            >
                              <button
                                type="button"
                                disabled={addingCardKey === mbKey}
                                onClick={() => handleQuickAddCard(staple.name, false)}
                                className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-600/80 hover:bg-purple-500 text-white border border-purple-400/40 flex items-center gap-0.5 transition-colors"
                              >
                                <Plus className="size-2.5" />
                                {addedCardKey === mbKey ? 'Added!' : addingCardKey === mbKey ? '...' : 'MAIN'}
                              </button>
                            </BrewTooltip>
                          )}
                          {onAddToSideboard && (
                            <BrewTooltip
                              title="Add to Sideboard (+SIDE)"
                              description={`Add 1 copy of ${staple.name} to your Sideboard / Command zone.`}
                              accent="amber"
                            >
                              <button
                                type="button"
                                disabled={addingCardKey === sbKey}
                                onClick={() => handleQuickAddCard(staple.name, true)}
                                className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-600/75 hover:bg-amber-500 text-white border border-amber-400/40 transition-colors"
                              >
                                {addedCardKey === sbKey ? '✓' : '+SIDE'}
                              </button>
                            </BrewTooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating Card Preview on Hover */}
      {previewCard && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: Math.min(window.innerWidth - 240, previewCard.x + 18),
            top: Math.max(12, Math.min(window.innerHeight - 320, previewCard.y - 140)),
          }}
        >
          <div className="w-[220px] rounded-xl overflow-hidden shadow-2xl border border-amber-500/40 bg-slate-950">
            <img src={previewCard.url} alt={previewCard.name} className="w-full h-auto block" />
          </div>
        </div>
      )}
    </div>
  );
}
